import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Wallet, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Smartphone,
  Loader2,
  RefreshCw,
  Copy,
  Info,
  QrCode,
  Building2,
  CreditCard,
  Trash2,
  Star,
  Send,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider logos/colors
const PROVIDERS = [
  { id: 'mpesa', name: 'M-Pesa', color: '#E60000', prefix: '067, 068' },
  { id: 'tigopesa', name: 'Tigo Pesa', color: '#004C8C', prefix: '065, 067, 071' },
  { id: 'airtel', name: 'Airtel Money', color: '#FF0000', prefix: '068, 078' },
  { id: 'halopesa', name: 'Halo Pesa', color: '#00A651', prefix: '062' },
];

export default function WalletPage() {
  const { t } = useLanguage();
  const { token } = useAuth();
  
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Cash In state
  const [showCashIn, setShowCashIn] = useState(false);
  const [cashInStep, setCashInStep] = useState(1);
  const [cashInData, setCashInData] = useState({ amount: '', provider: '', phone_number: '' });
  const [pendingTransaction, setPendingTransaction] = useState(null);
  
  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState({ amount: '', description: '' });
  const [activeCheckout, setActiveCheckout] = useState(null);
  
  // Withdraw state
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState(1);
  const [withdrawData, setWithdrawData] = useState({ amount: '', bank_account_id: '' });
  
  // Add Bank state
  const [showAddBank, setShowAddBank] = useState(false);
  const [bankFormData, setBankFormData] = useState({ bank_code: '', account_number: '', account_name: '' });
  
  const [processingPayment, setProcessingPayment] = useState(false);
  const [walletStatus, setWalletStatus] = useState({ simulation_enabled: false, withdrawable_balance: 0 });

  const fetchWallet = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
      const st = await axios.get(`${API_URL}/api/wallet/status`, { headers: { Authorization: `Bearer ${token}` } });
      setWalletStatus(st.data);
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
    }
  }, [token]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallet/transactions?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, [token]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    }
  }, [token]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    }
  }, [token]);

  const fetchCheckouts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/checkouts?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckouts(response.data.checkouts || []);
    } catch (error) {
      console.error('Failed to fetch checkouts:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchWallet(),
        fetchTransactions(),
        fetchBankAccounts(),
        fetchBanks(),
        fetchCheckouts()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchWallet, fetchTransactions, fetchBankAccounts, fetchBanks, fetchCheckouts]);

  const refreshAll = async () => {
    await Promise.all([
      fetchWallet(),
      fetchTransactions(),
      fetchBankAccounts(),
      fetchCheckouts()
    ]);
    toast.success(t('Refreshed!', 'Imesasishwa!'));
  };

  // Cash In handlers
  const handleCashIn = async () => {
    if (!cashInData.amount || parseFloat(cashInData.amount) < 1000) {
      toast.error(t('Minimum amount is TZS 1,000', 'Kiwango cha chini ni TZS 1,000'));
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallet/cash-in`, {
        amount: parseFloat(cashInData.amount),
        provider: cashInData.provider,
        phone_number: cashInData.phone_number
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setPendingTransaction(response.data);
      setCashInStep(3);
      toast.success(t('Payment initiated!', 'Malipo yameanzishwa!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to initiate payment', 'Imeshindwa kuanzisha malipo'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const simulateCashIn = async () => {
    if (!pendingTransaction) return;
    setProcessingPayment(true);
    try {
      await axios.post(`${API_URL}/api/wallet/simulate-payment/${pendingTransaction.transaction_id}`, {}, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Payment completed!', 'Malipo yamekamilika!'));
      resetCashIn();
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Payment failed', 'Malipo yameshindwa'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const resetCashIn = () => {
    setShowCashIn(false);
    setCashInStep(1);
    setCashInData({ amount: '', provider: '', phone_number: '' });
    setPendingTransaction(null);
  };

  // Checkout handlers
  const handleCreateCheckout = async () => {
    if (!checkoutData.amount || parseFloat(checkoutData.amount) < 500) {
      toast.error(t('Minimum amount is TZS 500', 'Kiwango cha chini ni TZS 500'));
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/checkout/create`, {
        amount: parseFloat(checkoutData.amount),
        description: checkoutData.description || 'Payment request'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setActiveCheckout(response.data);
      toast.success(t('Checkout created!', 'Checkout imetengenezwa!'));
      await fetchCheckouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to create checkout', 'Imeshindwa kutengeneza checkout'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const simulateCheckoutPayment = async (checkoutId) => {
    setProcessingPayment(true);
    try {
      await axios.post(`${API_URL}/api/checkout/${checkoutId}/simulate-payment`, {}, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Payment received!', 'Malipo yamepokelewa!'));
      setActiveCheckout(null);
      setShowCheckout(false);
      setCheckoutData({ amount: '', description: '' });
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed', 'Imeshindwa'));
    } finally {
      setProcessingPayment(false);
    }
  };

  // Bank account handlers
  const handleAddBankAccount = async () => {
    if (!bankFormData.bank_code || !bankFormData.account_number || !bankFormData.account_name) {
      toast.error(t('Please fill all fields', 'Tafadhali jaza sehemu zote'));
      return;
    }
    setProcessingPayment(true);
    try {
      await axios.post(`${API_URL}/api/bank-accounts`, bankFormData, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Bank account added!', 'Akaunti ya benki imeongezwa!'));
      setShowAddBank(false);
      setBankFormData({ bank_code: '', account_number: '', account_name: '' });
      await fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to add bank', 'Imeshindwa kuongeza benki'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    try {
      await axios.delete(`${API_URL}/api/bank-accounts/${accountId}`, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Bank account deleted', 'Akaunti imefutwa'));
      await fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed', 'Imeshindwa'));
    }
  };

  const handleSetDefaultBank = async (accountId) => {
    try {
      await axios.put(`${API_URL}/api/bank-accounts/${accountId}/set-default`, {}, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Default bank updated', 'Benki ya kawaida imesasishwa'));
      await fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed', 'Imeshindwa'));
    }
  };

  // Withdraw handlers
  const handleWithdraw = async () => {
    if (!withdrawData.amount || parseFloat(withdrawData.amount) < 10000) {
      toast.error(t('Minimum withdrawal is TZS 10,000', 'Kiwango cha chini cha kutoa ni TZS 10,000'));
      return;
    }
    if (!withdrawData.bank_account_id) {
      toast.error(t('Please select a bank account', 'Tafadhali chagua akaunti ya benki'));
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/withdraw`, {
        amount: parseFloat(withdrawData.amount),
        bank_account_id: withdrawData.bank_account_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(t('Withdrawal initiated!', 'Kutoa pesa kumeanzishwa!'));
      setShowWithdraw(false);
      setWithdrawStep(1);
      setWithdrawData({ amount: '', bank_account_id: '' });
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Withdrawal failed', 'Kutoa pesa kumeshindwa'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('Copied!', 'Imenakiliwa!'));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': case 'paid': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed': case 'expired': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'paid': return 'text-green-600 bg-green-50';
      case 'failed': case 'expired': return 'text-red-600 bg-red-50';
      default: return 'text-yellow-600 bg-yellow-50';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 pb-24">
        {/* Wallet Balance Card */}
        <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                <span className="text-sm opacity-90">{t('Wallet Balance', 'Salio la Pochi')}</span>
              </div>
              <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/10" onClick={refreshAll}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-3xl font-bold mb-2">{formatAmount(wallet?.balance || 0)}</div>
            <p className="mb-5 text-xs opacity-90" data-testid="wallet-demo-notice">
              {t('Demo wallet: real funding is coming soon. Pay UTT orders and Faida Control Numbers from their own pages. Only verified real deposits can be withdrawn.',
                'Pochi ya majaribio: kuweka pesa halisi kunakuja hivi karibuni. Lipia UTT na Faida kwenye kurasa zao. Ni amana halisi zilizothibitishwa tu zinazoweza kutolewa.')}
              {' '}{t('Withdrawable', 'Inayotolewa')}: <b data-testid="wallet-withdrawable">{formatAmount(walletStatus.withdrawable_balance)}</b>
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Button onClick={() => setShowCashIn(true)} className="bg-white text-primary hover:bg-white/90" data-testid="cash-in-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('Cash In', 'Weka')}
              </Button>
              <Button onClick={() => setShowCheckout(true)} className="bg-white/20 hover:bg-white/30" data-testid="checkout-btn">
                <QrCode className="w-4 h-4 mr-1" /> {t('Collect', 'Pokea')}
              </Button>
              <Button onClick={() => setShowWithdraw(true)} className="bg-white/20 hover:bg-white/30" data-testid="withdraw-btn">
                <Send className="w-4 h-4 mr-1" /> {t('Withdraw', 'Toa')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="overview">{t('History', 'Historia')}</TabsTrigger>
            <TabsTrigger value="checkouts">{t('Checkouts', 'Malipo')}</TabsTrigger>
            <TabsTrigger value="banks">{t('Banks', 'Benki')}</TabsTrigger>
            <TabsTrigger value="providers">{t('Top Up', 'Weka')}</TabsTrigger>
          </TabsList>

          {/* Transaction History */}
          <TabsContent value="overview" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {t('Transaction History', 'Historia ya Miamala')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('No transactions yet', 'Hakuna miamala bado')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.type === 'cash_in' || tx.type === 'checkout' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {tx.type === 'withdrawal' ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownLeft className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.created_at).toLocaleDateString()} • {tx.reference}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${tx.type === 'withdrawal' ? 'text-red-600' : 'text-green-600'}`}>
                            {tx.type === 'withdrawal' ? '-' : '+'}{formatAmount(tx.amount)}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(tx.status)}`}>
                            {tx.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Checkouts */}
          <TabsContent value="checkouts" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  {t('Payment Requests', 'Maombi ya Malipo')}
                </CardTitle>
                <Button size="sm" onClick={() => setShowCheckout(true)}>
                  <Plus className="w-4 h-4 mr-1" /> {t('New', 'Mpya')}
                </Button>
              </CardHeader>
              <CardContent>
                {checkouts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <QrCode className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('No payment requests yet', 'Hakuna maombi ya malipo bado')}</p>
                    <Button variant="outline" className="mt-3" onClick={() => setShowCheckout(true)}>
                      {t('Create Payment Request', 'Tengeneza Ombi la Malipo')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {checkouts.map((checkout) => (
                      <div key={checkout.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div>
                          <p className="font-medium">{formatAmount(checkout.amount)}</p>
                          <p className="text-xs text-muted-foreground">{checkout.reference}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(checkout.status)}`}>
                            {checkout.status}
                          </span>
                          {checkout.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => setActiveCheckout(checkout)}>
                              <QrCode className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Accounts */}
          <TabsContent value="banks" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {t('Bank Accounts', 'Akaunti za Benki')}
                </CardTitle>
                <Button size="sm" onClick={() => setShowAddBank(true)}>
                  <Plus className="w-4 h-4 mr-1" /> {t('Add', 'Ongeza')}
                </Button>
              </CardHeader>
              <CardContent>
                {bankAccounts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('No bank accounts linked', 'Hakuna akaunti za benki zilizounganishwa')}</p>
                    <Button variant="outline" className="mt-3" onClick={() => setShowAddBank(true)}>
                      {t('Add Bank Account', 'Ongeza Akaunti ya Benki')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bankAccounts.map((account) => (
                      <div key={account.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{account.bank_name}</p>
                              {account.is_default && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {account.account_name} • ****{account.account_number.slice(-4)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!account.is_default && (
                            <Button size="icon" variant="ghost" onClick={() => handleSetDefaultBank(account.id)}>
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteBankAccount(account.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Top Up */}
          <TabsContent value="providers" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('Quick Top Up', 'Weka Pesa Haraka')}</CardTitle>
                <CardDescription>{t('Select your mobile money provider', 'Chagua mtoa huduma wako')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {PROVIDERS.map((provider) => (
                    <button
                      key={provider.id}
                      onClick={() => {
                        setCashInData(prev => ({ ...prev, provider: provider.id }));
                        setShowCashIn(true);
                        setCashInStep(1);
                      }}
                      className="p-4 rounded-xl border-2 border-border hover:border-primary/50 transition-colors text-center"
                    >
                      <div 
                        className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: provider.color }}
                      >
                        {provider.name.charAt(0)}
                      </div>
                      <span className="font-medium">{provider.name}</span>
                      <p className="text-xs text-muted-foreground">{provider.prefix}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cash In Dialog */}
      <Dialog open={showCashIn} onOpenChange={(open) => !open && resetCashIn()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {cashInStep === 1 && t('Enter Amount', 'Ingiza Kiasi')}
              {cashInStep === 2 && t('Select Provider', 'Chagua Mtoa Huduma')}
              {cashInStep === 3 && t('Complete Payment', 'Kamilisha Malipo')}
            </DialogTitle>
          </DialogHeader>

          {cashInStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('Amount (TZS)', 'Kiasi (TZS)')}</Label>
                <Input
                  type="number"
                  placeholder="10,000"
                  value={cashInData.amount}
                  onChange={(e) => setCashInData(prev => ({ ...prev, amount: e.target.value }))}
                  className="text-2xl h-14 text-center font-semibold"
                  data-testid="cash-in-amount-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[10000, 50000, 100000, 250000, 500000, 1000000].map((amount) => (
                  <Button key={amount} variant="outline" size="sm"
                    onClick={() => setCashInData(prev => ({ ...prev, amount: amount.toString() }))}
                    className={cashInData.amount === amount.toString() ? 'border-primary bg-primary/5' : ''}>
                    {amount >= 1000000 ? `${amount/1000000}M` : `${amount/1000}K`}
                  </Button>
                ))}
              </div>
              <Button className="w-full" onClick={() => setCashInStep(2)}
                disabled={!cashInData.amount || parseFloat(cashInData.amount) < 1000}>
                {t('Continue', 'Endelea')}
              </Button>
            </div>
          )}

          {cashInStep === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {PROVIDERS.map((provider) => (
                  <button key={provider.id}
                    onClick={() => setCashInData(prev => ({ ...prev, provider: provider.id }))}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      cashInData.provider === provider.id ? 'border-primary bg-primary/5' : 'border-border'
                    }`}>
                    <div className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center text-white font-bold text-xs"
                      style={{ backgroundColor: provider.color }}>{provider.name.charAt(0)}</div>
                    <span className="text-sm font-medium">{provider.name}</span>
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <Label>{t('Phone Number', 'Nambari ya Simu')}</Label>
                <Input type="tel" placeholder="0712 345 678" value={cashInData.phone_number}
                  onChange={(e) => setCashInData(prev => ({ ...prev, phone_number: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCashInStep(1)} className="flex-1">{t('Back', 'Rudi')}</Button>
                <Button className="flex-1" onClick={handleCashIn}
                  disabled={!cashInData.provider || !cashInData.phone_number || processingPayment}>
                  {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Initiate', 'Anzisha')}
                </Button>
              </div>
            </div>
          )}

          {cashInStep === 3 && pendingTransaction && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">{t('Amount', 'Kiasi')}</p>
                <p className="text-2xl font-bold text-primary">{formatAmount(pendingTransaction.amount)}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">{t('Reference', 'Kumbukumbu')}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-yellow-100 px-2 py-1 rounded">{pendingTransaction.reference}</code>
                      <button onClick={() => copyToClipboard(pendingTransaction.reference)}><Copy className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
              <pre className="text-sm bg-muted/30 p-3 rounded-lg whitespace-pre-wrap">{pendingTransaction.instructions}</pre>
              <Button onClick={simulateCashIn} className="w-full" disabled={processingPayment || !walletStatus.simulation_enabled} data-testid="simulate-cashin-btn">
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {t('Simulate Payment', 'Jaribu Malipo')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Create Payment Request', 'Tengeneza Ombi la Malipo')}</DialogTitle>
            <DialogDescription>{t('Generate a QR code or USSD for customers to pay you', 'Tengeneza QR code au USSD kwa wateja kukulipa')}</DialogDescription>
          </DialogHeader>
          
          {!activeCheckout ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('Amount (TZS)', 'Kiasi (TZS)')}</Label>
                <Input type="number" placeholder="5,000" value={checkoutData.amount}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, amount: e.target.value }))}
                  className="text-xl h-12 text-center font-semibold" />
              </div>
              <div className="space-y-2">
                <Label>{t('Description (Optional)', 'Maelezo (Si lazima)')}</Label>
                <Input placeholder={t('e.g. Coffee payment', 'mf. Malipo ya kahawa')} value={checkoutData.description}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <Button className="w-full" onClick={handleCreateCheckout} disabled={processingPayment || !checkoutData.amount}>
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                {t('Generate Payment QR', 'Tengeneza QR ya Malipo')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg flex justify-center">
                <QRCodeSVG value={activeCheckout.qr_code_data} size={200} />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{formatAmount(activeCheckout.amount)}</p>
                <p className="text-sm text-muted-foreground">{activeCheckout.reference}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium mb-1">{t('USSD Code', 'Msimbo wa USSD')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-background px-3 py-2 rounded">{activeCheckout.ussd_code}</code>
                  <Button size="icon" variant="outline" onClick={() => copyToClipboard(activeCheckout.ussd_code)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={() => simulateCheckoutPayment(activeCheckout.checkout_id)} className="w-full" disabled={processingPayment || !walletStatus.simulation_enabled} data-testid="simulate-checkout-btn">
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {t('Simulate Payment', 'Jaribu Malipo')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code View Dialog */}
      <Dialog open={!!activeCheckout && !showCheckout} onOpenChange={() => setActiveCheckout(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Payment QR Code', 'QR Code ya Malipo')}</DialogTitle>
          </DialogHeader>
          {activeCheckout && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg flex justify-center">
                <QRCodeSVG value={activeCheckout.qr_code_data} size={200} />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{formatAmount(activeCheckout.amount)}</p>
                <p className="text-sm text-muted-foreground">{activeCheckout.reference}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Withdraw to Bank', 'Toa kwa Benki')}</DialogTitle>
            <DialogDescription>{t('Transfer funds to your bank account', 'Hamisha pesa kwenye akaunti yako ya benki')}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground">{t('Withdrawable (verified real deposits)', 'Inayotolewa (amana halisi zilizothibitishwa)')}</p>
              <p className="text-xl font-bold" data-testid="withdraw-available">{formatAmount(walletStatus.withdrawable_balance)}</p>
              <p className="text-xs text-muted-foreground">{t('Withdrawals are reviewed by our team before payout.', 'Utoaji hukaguliwa na timu yetu kabla ya kulipwa.')}</p>
            </div>
            
            <div className="space-y-2">
              <Label>{t('Amount (TZS)', 'Kiasi (TZS)')}</Label>
              <Input type="number" placeholder="100,000" value={withdrawData.amount}
                onChange={(e) => setWithdrawData(prev => ({ ...prev, amount: e.target.value }))}
                className="text-xl h-12 text-center font-semibold" />
              <p className="text-xs text-muted-foreground">
                {t('Min: TZS 10,000 • Fee: 1% (min 500, max 10,000)', 'Kiwango cha chini: TZS 10,000 • Ada: 1%')}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('Bank Account', 'Akaunti ya Benki')}</Label>
              {bankAccounts.length === 0 ? (
                <Button variant="outline" className="w-full" onClick={() => { setShowWithdraw(false); setShowAddBank(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> {t('Add Bank Account First', 'Ongeza Akaunti ya Benki Kwanza')}
                </Button>
              ) : (
                <Select value={withdrawData.bank_account_id} onValueChange={(v) => setWithdrawData(prev => ({ ...prev, bank_account_id: v }))}>
                  <SelectTrigger><SelectValue placeholder={t('Select bank account', 'Chagua akaunti')} /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.bank_name} - ****{acc.account_number.slice(-4)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {withdrawData.amount && parseFloat(withdrawData.amount) >= 10000 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span>{t('Amount', 'Kiasi')}</span>
                  <span>{formatAmount(parseFloat(withdrawData.amount))}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{t('Fee', 'Ada')}</span>
                  <span>{formatAmount(Math.max(500, Math.min(10000, parseFloat(withdrawData.amount) * 0.01)))}</span>
                </div>
                <div className="flex justify-between font-medium mt-1 pt-1 border-t">
                  <span>{t('You Receive', 'Utapokea')}</span>
                  <span className="text-green-600">{formatAmount(parseFloat(withdrawData.amount))}</span>
                </div>
              </div>
            )}

            <Button className="w-full" onClick={handleWithdraw}
              disabled={processingPayment || !withdrawData.amount || !withdrawData.bank_account_id || parseFloat(withdrawData.amount) < 10000}>
              {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {t('Withdraw', 'Toa Pesa')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Bank Dialog */}
      <Dialog open={showAddBank} onOpenChange={setShowAddBank}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Add Bank Account', 'Ongeza Akaunti ya Benki')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('Bank', 'Benki')}</Label>
              <Select value={bankFormData.bank_code} onValueChange={(v) => setBankFormData(prev => ({ ...prev, bank_code: v }))}>
                <SelectTrigger><SelectValue placeholder={t('Select bank', 'Chagua benki')} /></SelectTrigger>
                <SelectContent>
                  {banks.map((bank) => (
                    <SelectItem key={bank.code} value={bank.code}>{bank.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('Account Number', 'Nambari ya Akaunti')}</Label>
              <Input placeholder="1234567890" value={bankFormData.account_number}
                onChange={(e) => setBankFormData(prev => ({ ...prev, account_number: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>{t('Account Name', 'Jina la Akaunti')}</Label>
              <Input placeholder="John Doe" value={bankFormData.account_name}
                onChange={(e) => setBankFormData(prev => ({ ...prev, account_name: e.target.value }))} />
            </div>

            <Button className="w-full" onClick={handleAddBankAccount} disabled={processingPayment}>
              {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {t('Add Bank Account', 'Ongeza Akaunti')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
